// otpsSlice.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PUBLIC_API_URL } from '@src/config';
import axios from 'axios';

/**
 * Utility function to map OTP data response properly
 */
const mapOtpData = (otpData) => ({
  otpNo: otpData.otpNo || null,
  otpDetails: {
    company: otpData.company || null,
    currency: otpData.currency || null,
    contact: otpData.contact || null,
    terms: otpData.terms || null,
    otpAuth: otpData.otpAuth || '',
    exchangeRate: otpData.exchangeRate || 0,
    comments: otpData.comments || '',
    date: otpData.date ? new Date(otpData.date).toISOString().split('T')[0] : null,
    rep: otpData.rep || null,
    status: otpData.status || 'stage1',
    salesReps: otpData.salesReps || [],
    ref: otpData.ref || ''
  },
  calculateValues: {
    type: otpData?.type || 'franchise',
    percentage: otpData?.percentage || 10,
    preferredTotal: otpData?.salesTotal || 0
  },
  table: otpData.items.map((item) => ({
    expanded: false,
    ...item
  }))
});

// Async thunks
export const fetchOTPById = createAsyncThunk(
  'otp/fetchOTPById',
  async (otpNo, thunkAPI) => {
    try {
      const url = `${PUBLIC_API_URL}/api/otps/${otpNo}`;
      const { data } = await axios.get(url);
      return mapOtpData(data.data);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const fetchPreferredTotal = createAsyncThunk(
  'otp/fetchPreferredTotal',
  async (data, thunkAPI) => {
    const { otpNo, selectedRows } = data;
    try {
      const url = `${PUBLIC_API_URL}/api/otps/${otpNo}/total`;
      const { data } = await axios.post(url, { selectedItems: [...selectedRows] });
      return data.data.total;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Initial state
const initialState = {
  otpNo: null,
  otpDetails: {
    company: null,
    currency: null,
    contact: null,
    terms: null,
    otpAuth: '',
    exchangeRate: 0,
    comments: '',
    date: new Date(),
    rep: null,
    status: 'stage1',
    salesReps: [],
    ref: ''
  },
  table: [],
  calculateValues: {
    type: 'franchise',
    percentage: 10,
    totalSale: 0,
    preferredTotal: 0
  },
  selectedRows: new Set(),
  searchQuery: '',
  itemsLoaded: 0,
  loading: false,
  error: null,
  unsavedChanges: false,
  focusMode: false 
};

// Slice creation
const otpsSlice = createSlice({
  name: 'otps',
  initialState,
  reducers: {
    updateOtpField: (state, action) => {
      const { field, value } = action.payload;
      state.otpDetails[field] = value;
      state.unsavedChanges = true;
    },
    setOtpNo: (state, action) => {
      state.otpNo = action.payload;
      state.unsavedChanges = true;
    },
    addLine: (state, action) => {
      state.table.push(action.payload);
      state.unsavedChanges = true;
    },
    removeLine: (state, action) => {
      state.table = state.table.filter((_, index) => index !== action.payload);
      state.unsavedChanges = true;
    },
    setTable: (state, action) => {
      state.table = action.payload;
      state.unsavedChanges = true;
    },
    setSelectedRows: (state, action) => {
      state.selectedRows = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setItemsLoaded: (state, action) => {
      state.itemsLoaded = action.payload;
    },
    setCalculateValues: (state, action) => {
      state.calculateValues = action.payload;
      state.unsavedChanges = true;
    },
    resetOTP: () => initialState,
    // Reducer to toggle focus mode:
    toggleFocusMode: (state) => {
      state.focusMode = !state.focusMode;
    },

    initializeNewOTP: (state, action) => {
      return {
        ...initialState,
        otpDetails: {
          ...initialState.otpDetails,
          salesReps: [{ value: action.payload.employeeNo, label: action.payload.employeeName }]
        }
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOTPById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOTPById.fulfilled, (state, action) => {
        state.loading = false;
        const otpData = action.payload;
        state.otpNo = otpData.otpNo;
        state.otpDetails = otpData.otpDetails;
        state.calculateValues = otpData.calculateValues;
        state.table = otpData.table;
        state.unsavedChanges = false;
      })
      .addCase(fetchOTPById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      })
      .addCase(fetchPreferredTotal.fulfilled, (state, action) => {
        state.calculateValues.preferredTotal = action.payload;
      })
      .addCase(fetchPreferredTotal.rejected, (state, action) => {
        state.error = action.payload.error;
      });
  }
});

// Export actions and reducer
export const {
  setOtpNo,
  updateOtpField,
  setTable,
  addLine,
  removeLine,
  setSelectedRows,
  setSearchQuery,
  setItemsLoaded,
  resetOTP,
  setCalculateValues,
  toggleFocusMode,
  initializeNewOTP
} = otpsSlice.actions;

export default otpsSlice.reducer;
